/**
 * Auth Context Provider
 *
 * Provides authentication state and methods throughout the React app.
 * Manages AWS Cognito (OIDC) authentication; token refresh is handled
 * automatically by oidc-client-ts silent renew.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthUser, useCognitoAuth } from './CognitoAuth';

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  token: string | null;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, token } = useCognitoAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    console.log('🔍 AuthContext: user changed', { user, hasUser: !!user, loading });
    setIsAuthenticated(!!user);
  }, [user, loading]);

  const value: AuthContextType = {
    user,
    loading,
    token,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
